<template>
  <div class="a-cover header-padding">
    <Transition name="fade">
      <Header class="z-10" />
    </Transition>

    <div
      ref="focusTarget"
      :aria-hidden="mediaOpen || isGuidedIntro"
      class="relative h-full"
    >
      <Transition name="fade">
        <KeepAlive>
          <!-- Renders the 'Categories' when on the campus -->
          <Categories
            v-if="isCampus && !mediaOpen"
            :category="category"
            class="z-1"
          />

          <!-- Renders when in panorama groups -->
          <Sidebar
            v-else-if="isPanorama && !mediaOpen"
            :post="post"
            :location="location"
            :is-guided="isGuided"
            :category="category"
            :palette="palette"
            class="z-1"
            @hotSpotClick="handleNavigation"
          />
        </KeepAlive>
      </Transition>

      <Transition name="slide-down" appear>
        <!-- Shows the 360 title when in a group and the sidebar is collapsed -->
        <span
          v-show="!sidebarOpen && !isCampus && !isGuidedIntro && !mediaOpen"
          class="absolute top-0 flex-c w-full h-6 font-serif italic z-1 transition xs-l:hidden"
          :class="[`bg-${palette.primary}`, { 'text-white': isGuided }]"
          v-html="post.title"
        />
      </Transition>

      <TransitionGroup name="fade">
        <Panorama
          v-show="!isGuidedIntro"
          key="panorama"
          :post="post"
          :location="location"
          :category="category"
          :palette="palette"
          :is-campus="isCampus"
          class="z-0"
          @hotSpotClick="handleNavigation"
        />
        <TourStops
          v-show="!mobile && !isGuidedIntro && isGuided"
          key="tourstops"
          :titles="false"
          class="absolute lg:pin-x lg:bottom-6 lg:w-60% lg:ml-48"
        />
      </TransitionGroup>
    </div>

    <Transition name="fade">
      <FocusLock v-if="mediaOpen || isGuidedIntro">
        <!-- Handles the Articles Videos etc as an overlay -->
        <RouterView
          name="media"
          :location="location"
          :palette="palette"
          class="z-1"
        />

        <!-- Shows the guided tour into page as an overlay -->
        <TourIntro v-if="isGuidedIntro" class="z-1" />
      </FocusLock>
    </Transition>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { loadKrp } from '@/mixins'
import Panorama from '@/components/Panorama.vue'
import Header from '@/components/Header.vue'
import FocusLock from 'vue-focus-lock'
const TourStops = () => import('@/components/TourStops.vue')
const Sidebar = () => import('@/components/Sidebar.vue')
const TourIntro = () => import('@/components/TourIntro.vue')
const Categories = () => import('@/components/Categories.vue')

export default {
  name: 'Viewer',

  components: {
    FocusLock,
    Panorama,
    Header,
    Sidebar,
    TourIntro,
    Categories,
    TourStops
  },

  mixins: [loadKrp],

  computed: {
    ...mapState(['touch', 'mobile', 'mediaOpen', 'sidebarOpen']),
    ...mapGetters(['categories', 'getPostBySlug', 'getCampus', 'guidedtour']),

    category() {
      return this.getPostBySlug(this.$route.params.category_id)
    },

    location() {
      // Locations are 360 groups - the group is defined by media_id when in a 360
      const location = this.getPostBySlug(this.$route.params.media_id)
      // Filter 360s in the group by parents
      if (location?.type === 'location') {
        // If no parents include anyway as is considered unfiltered
        const unfiltered = location.children.filter(item => !item.parents)
        // If there is a parent or parents does the array include the current category id?
        const filtered = location.children.filter(item =>
          item.parents.includes(this.category.id)
        )

        const result = Object.assign({}, location, {
          children: [...filtered, ...unfiltered]
        })

        return result
      }

      return this.guidedtour
    },

    campus() {
      return this.getCampus(this.$route.params.category_id)
    },

    panorama() {
      return this.getPostBySlug(this.$route.params.panorama_id)
    },

    post() {
      if (this.isGuidedIntro) return this.guidedtour.children[0]
      if (this.isCampus) return this.campus
      return this.panorama
    },

    isCampus() {
      return this.$route.meta.campus
    },

    isGuided() {
      return this.$route.meta.guided
    },

    isGuidedIntro() {
      return this.$route.name === 'guided-tour'
    },

    isPanorama() {
      return this.$route.meta.panorama
    },

    palette() {
      if (this.isGuided) return this.guidedtour.palette
      return this.category.palette
    }
  },

  methods: {
    handleNavigation(slug) {
      if (this.isCampus) {
        const location = this.getPostBySlug(slug)
        const children = location.children

        if (children.length) {
          this.$router
            .push({
              name: 'campus-panorama',
              params: {
                category_id: this.category.slug,
                media_id: location.slug,
                panorama_id: children[0].slug
              }
            })
            .catch(() => {})
        }
      } else if (this.isGuided) {
        this.$router
          .push({
            name: 'guided-hotspot',
            params: {
              panorama_id: this.post.slug,
              hotspot_id: slug
            }
          })
          .catch(() => {})
      } else if (this.isPanorama) {
        this.$router
          .push({
            name: 'campus-hotspot',
            params: {
              category_id: this.category.slug,
              panorama_id: this.post.slug,
              hotspot_id: slug
            }
          })
          .catch(() => {})
      }
    }
  }
}
</script>
